import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Select from '../../../ui/Select';
import ProgramCard from './ProgramCard';
import StartupCard from './StartupCard';

const MatchingResults = ({ 
  results = [], 
  resultType = 'programs', 
  loading = false, 
  userRole = 'startup',
  onViewDetails,
  onApply,
  onContact,
  onViewProfile 
}) => {
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');

  const sortOptions = [
    { value: 'relevance', label: 'Best Match' },
    { value: 'newest', label: 'Newest First' },
    { value: 'deadline', label: 'Deadline Soon' },
    { value: 'funding', label: 'Highest Funding' },
    { value: 'popularity', label: 'Most Popular' }
  ];

  const getSortedResults = () => {
    if (!results || results?.length === 0) return [];
    
    const sorted = [...results];
    switch (sortBy) {
      case 'relevance':
        return sorted?.sort((a, b) => (b?.matchScore || 0) - (a?.matchScore || 0));
      case 'newest':
        return sorted?.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case 'deadline':
        return sorted?.sort((a, b) => new Date(a.deadline || '9999-12-31') - new Date(b.deadline || '9999-12-31'));
      case 'funding':
        return sorted?.sort((a, b) => {
          const aFunding = parseInt(a?.funding?.replace(/[^0-9]/g, '') || '0');
          const bFunding = parseInt(b?.funding?.replace(/[^0-9]/g, '') || '0');
          return bFunding - aFunding;
        });
      case 'popularity':
        return sorted?.sort((a, b) => (b?.applications || b?.views || 0) - (a?.applications || a?.views || 0));
      default:
        return sorted;
    }
  };

  const sortedResults = getSortedResults();

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="flex items-center justify-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Finding matches...</span>
        </div>
      </div>
    );
  }

  if (!results || results?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No matches found</h3>
        <p className="text-muted-foreground mb-4">
          Try adjusting your search criteria or filters to find more results.
        </p>
        <Button variant="outline" iconName="RefreshCw" iconPosition="left">
          Refresh Search
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {results?.length} {resultType === 'programs' ? 'Programs' : 'Startups'} Found
              </h2>
              <p className="text-sm text-muted-foreground">
                Showing results based on your criteria
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-muted rounded-md p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Grid3X3" size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="List" size={16} />
              </button>
            </div>
            
            {/* Sort Dropdown */}
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              className="w-40"
            />
          </div>
        </div>
      </div>
      {/* Results Grid/List */}
      <div className={
        viewMode === 'grid' ?'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6' :'space-y-4'
      }>
        {sortedResults?.map((item, index) => (
          <div key={item?.id || index}>
            {resultType === 'programs' ? (
              <ProgramCard
                program={item}
                onViewDetails={onViewDetails}
                onApply={onApply}
                userRole={userRole}
              />
            ) : (
              <StartupCard
                startup={item}
                onViewProfile={onViewProfile}
                onContact={onContact}
                userRole={userRole}
              />
            )}
          </div>
        ))}
      </div>
      {/* Load More */}
      {results?.length >= 20 && (
        <div className="text-center">
          <Button
            variant="outline"
            iconName="ChevronDown"
            iconPosition="right"
          >
            Load More Results
          </Button>
        </div>
      )}
    </div>
  );
};

export default MatchingResults;